import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { i18n } from '@fs/zion-locale'
import { useTranslation, Trans } from 'react-i18next'
import { colors, useOverlay, DialogOverlay, Paragraph } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'

const buttonCss = css`
  align-items: center;
  color: ${colors.gray100};
  background: none;
  border: none;
  font-size: 12px;
  padding: 0;

  // match footer legal links
  & {
    text-decoration: underline;
  }

  // to match global link style
  &:focus-visible {
    outline: solid currentcolor;
    outline-width: 3px;
    outline-offset: 3px;
    border-radius: 2px;
  }
`
const trustArcId = 'teconsent'
const hasDocument = typeof document !== 'undefined'

const loadScript = (src) => {
  if (hasDocument) {
    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (!existingScript) {
      const tag = document.createElement('script')
      tag.async = true
      tag.src = src
      document.getElementsByTagName('head')[0].appendChild(tag)
    }
  } else {
    console.error('Document is not available - cookie preferences script not loaded')
  }
}

/**
 * trustArc banner needs this injection div
 */
const insertConsentBlackbarElement = () => {
  if (hasDocument) {
    const existingDiv = document.getElementById('consent_blackbar')
    if (!existingDiv) {
      const div = document.createElement('div')
      div.id = 'consent_blackbar'
      document.body.appendChild(div)
    }
  } else {
    console.error('Document is not available - consent blackbar div not inserted')
  }
}

/**
 * insert the element that trustArc uses to handle the cookie preferences pops
 */
const insertTeConsentElement = () => {
  if (hasDocument) {
    const existingDiv = document.getElementById(trustArcId)
    if (!existingDiv) {
      const div = document.createElement('div')
      div.id = trustArcId

      // styles directly on the element get removed
      // so do this to hide the clickable element
      const style = document.createElement('style')
      style.innerHTML = `
        #${trustArcId} {
          display: none !important;
          visibility: hidden !important;
          height: 0px !important;
          width: 0px !important;
          position: absolute !important;
          overflow: hidden !important;
        }
      `
      document.head.appendChild(style)
      document.body.appendChild(div)
    }
  } else {
    console.error('Document is not available - teconsent element not inserted')
  }
}

/**
 * Finds the TrustArc injected element and clicks the link inside of it
 * @returns The clicked element, or undefined if not found
 */
export const handleTrustArcClick = () => {
  const trustArcElement = document.getElementById(trustArcId)
  const injectedClickableElement = trustArcElement?.querySelector('a')
  injectedClickableElement?.click()
  return injectedClickableElement
}

/**
 * CookiePreferences
 * Opens the TrustArc cookie preferences dialog and handles error states.
 *
 * @param {Object} props - Component props.
 * @param {boolean} [props.includeButton=true] - When true, renders a built‑in button to open the dialog. Set to false to supply a custom clickable child.
 * @param {() => void} [props.onClick] - Optional callback invoked before attempting to open the TrustArc dialog (e.g., analytics). Does not receive the event.
 * @param {import('react').ReactElement} [props.children] - Custom clickable child used when `includeButton` is false. The child’s own `onClick` is preserved and called first.
 */
export default function CookiePreferences({ includeButton = true, onClick, children }) {
  const [t] = useTranslation()
  const buttonText = t('footer.cookie-preferences', 'Cookie Preferences')
  const ariaLabel = t(
    'footer.cookie-preferences-aria-label',
    'Cookie Preferences, opens a dedicated popup modal window'
  )
  const errorOverlay = useOverlay()
  errorOverlay.closeOnClickAway = false

  useEffect(() => {
    // TrustArc cookie prefs banner and overlay
    let trusteLang = i18n.language
    if (trusteLang === 'zh') {
      trusteLang = 'ZH-Hant'
    } else if (trusteLang === 'zh-hans') {
      trusteLang = 'zh-cn'
    }
    const scriptUrl = `//consent.trustarc.com/notice?domain=familysearchv2.org&c=teconsent&js=nj&noticeType=bb&text=true&gtm=1&pcookie&pn=1-0&cdn=1&language=${trusteLang}`

    insertTeConsentElement()
    insertConsentBlackbarElement()
    loadScript(scriptUrl)
  }, [])

  // Click the trustarc element injected into the body
  // If it isn't found, open the error overlay
  const handleClick = () => {
    if (onClick) {
      onClick()
    }

    const openedTrustArcDialog = handleTrustArcClick()
    if (!openedTrustArcDialog) {
      errorOverlay.open()
      console.error('Unable to open TrustArc dialog')
    }
  }

  // If rendering a custom child, inject an onClick into it
  const childWithOnClick = React.isValidElement(children)
    ? React.cloneElement(children, {
        onClick: (e) => {
          if (typeof children.props?.onClick === 'function') {
            children.props.onClick(e)
          }
          handleClick()
        },
      })
    : children

  return (
    <>
      {includeButton ? (
        <button
          className={buttonCss}
          onClick={handleClick}
          data-testid="cookiePreferences"
          type="button"
          aria-haspopup="dialog"
          aria-label={ariaLabel}
        >
          {buttonText}
        </button>
      ) : (
        childWithOnClick
      )}
      <DialogOverlay
        data-testid="cookie-error-dialog"
        {...errorOverlay}
        title={t('footer.cookie-preferences-error', 'Oops! Something went wrong')}
      >
        <Paragraph size="sm">
          {t('footer.cookie-preferences-error-msg', 'We were unable to load the cookie preferences.')}
        </Paragraph>
        <Paragraph size="sm">
          {t('footer.cookie-preferences-error-msg-refresh', 'Please try again in a moment or refresh the page.')}
        </Paragraph>
        <Paragraph size="sm">
          <Trans
            i18nKey="footer.cookie-preferences-error-support"
            defaults="If the problem persists, please <0>contact support</0>."
            components={[<Link to="/help" key="link" />]}
          />
        </Paragraph>
      </DialogOverlay>
    </>
  )
}

CookiePreferences.propTypes = {
  // Whether to include the cookie prefs button provided.
  // True by default. If false, you must pass in your own link/button, see docs
  includeButton: PropTypes.bool,

  // A callback function when the link is clicked, e.g. for analytics
  // only used in the built-in button scenario
  onClick: PropTypes.func,

  // Custom clickable child used when `includeButton` is false.
  children: PropTypes.element,
}
